import React, { useState, useEffect } from "react";
import Icon from "components/Elements/Icon/Icon.tsx";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import { useToasts } from "react-toast-notifications";
import Image from "components/Elements/Image/Image.tsx";
import { useUser } from "contexts/userContext/userContext.js";
import { useModal } from "contexts/modalContext/modalContext.js";
import EmbedLimitOptionsModal from "components/Reusable/JUMBOform/Modals/EmbedLimitOptionsModal.js";

const DesignTool = ({
  formBackgroundColor,
  setFormBackgroundColor,
  setFormWidth,
  formWidth,
  setUpdateBeforeSave,
  formMaxHeight,
  setFormMaxHeight,
}) => {
  const { setModal } = useModal();
  const { addToast } = useToasts();
  const { getTextByKey } = useContent();
  const { isPremium } = useUser();
  //Background:
  const [updateBackgroundColor, setUpdateBackgroundColor] = useState(false);
  const [backgroundColorText, setbackgroundColorText] =
    useState(formBackgroundColor);
  const [tempFocusBackgroundText, setTempFocusBackgroundText] = useState(null);
  const [tempBlurBackgroundText, setTempBlurBackgroundText] = useState(null);
  const [tempInputBackgroundFocus, setTempInputBackgroundFocus] =
    useState(null);
  const [tempInputBackgroundBlur, setTempInputBackgroundBlur] = useState(null);

  //Width:
  const [oldWidth, setOldWidth] = useState(formWidth);
  const [invalidWidth, setInvalidWidth] = useState(false);
  const [tempFocusWidth, setTempFocusWidth] = useState(0);
  const [tempBlurWidth, setTempBlurWidth] = useState(0);

  //Max Height:
  const [oldMaxHeight, setOldMaxHeight] = useState(formMaxHeight);
  const [invalidMaxHeight, setInvalidMaxHeight] = useState(false);
  const [tempFocusMaxHeight, setTempFocusMaxHeight] = useState(0);
  const [tempBlurMaxHeight, setTempBlurMaxHeight] = useState(0);

  //save width on blur if it changed
  useEffect(() => {
    if (tempBlurWidth !== tempFocusWidth && !invalidWidth) {
      setOldWidth(formWidth);
      setUpdateBeforeSave({ mode: true, action: "updateWidth" });
    } else if (invalidWidth) {
      setFormWidth(oldWidth);
    }
  }, [tempBlurWidth]);

  const handleFocusWidth = (e) => {
    if (isPremium()) {
      setTempFocusWidth(e);
    }
  };
  const handleBlurWidth = (e) => {
    if (isPremium()) {
      setTempBlurWidth(e);
    }
  };

  useEffect(() => {
    const validWidth = new RegExp("^[0-9]*$");
    if (validWidth.test(formWidth)) {
      if (formWidth < 460) {
        setInvalidWidth(true);
      } else if (formWidth > 1000) {
        setInvalidWidth(true);
      } else {
        setInvalidWidth(false);
      }
    } else {
      setInvalidWidth(true);
    }
  }, [formWidth]);

  //save max height on blur if it changed
  useEffect(() => {
    if (tempBlurMaxHeight !== tempFocusMaxHeight && !invalidMaxHeight) {
      setOldMaxHeight(formMaxHeight);
      setUpdateBeforeSave({ mode: true, action: "updateMaxHeight", heightChanged: true });
    } else if (invalidMaxHeight) {
      setFormMaxHeight(oldMaxHeight);
    }
  }, [tempBlurMaxHeight]);

  const handleFocusMaxHeight = (e) => {
    if (isPremium()) {
      setTempFocusMaxHeight(e);
    }
  };
  const handleBlurMaxHeight = (e) => {
    if (isPremium()) {
      setTempBlurMaxHeight(e);
    }
  };

  useEffect(() => {
    const validMaxHeight = new RegExp("^[0-9]*$");
    if (validMaxHeight.test(formMaxHeight)) {
      if (formMaxHeight < 450 && formMaxHeight > 0) {
        setInvalidMaxHeight(true);
      } else {
        setInvalidMaxHeight(false);
      }
    } else {
      setInvalidMaxHeight(true);
    }
  }, [formMaxHeight]);

  //Background Functions:

  useEffect(() => {
    if (updateBackgroundColor) {
      setUpdateBeforeSave({ mode: true, action: "updateBackgroundColor" });
      setUpdateBackgroundColor(false);
    }
  }, [updateBackgroundColor]);

  //Text input:
  const handleChangeBackgroundColorText = (e) => {
    if (!isPremium()) {
      setModal(() => <EmbedLimitOptionsModal />);
    } else {
      if (e.startsWith("#")) {
        setbackgroundColorText(e);
      }
    }
  };

  const handleFocusBackgroundColorText = (e) => {
    if (isPremium()) {
      if (e.startsWith("#")) {
        setTempFocusBackgroundText(e);
      }
    }
  };

  const handleBlurBackgroundColorText = (e) => {
    if (isPremium()) {
      if (e.startsWith("#")) {
        setTempBlurBackgroundText(e);
      }
    }
  };

  useEffect(() => {
    if (tempBlurBackgroundText !== tempFocusBackgroundText) {
      const validColorCode = new RegExp("^#(?:[0-9a-fA-F]{3}){1,2}$");
      if (validColorCode.test(backgroundColorText)) {
        setFormBackgroundColor(backgroundColorText);
        setUpdateBackgroundColor(true);
      } else {
        addToast(getTextByKey("invalid-color-code"), {
          appearance: "error",
        });
        setbackgroundColorText(formBackgroundColor);
      }
    }
  }, [tempBlurBackgroundText]);

  useEffect(() => {
    if (formBackgroundColor) {
      setbackgroundColorText(formBackgroundColor);
    }
  }, [formBackgroundColor]);

  const handleChangeBackgroundColor = (e) => {
    if (!isPremium()) {
      setModal(() => <EmbedLimitOptionsModal />);
    } else {
      setFormBackgroundColor(e);
    }
  };

  useEffect(() => {
    if (tempInputBackgroundBlur !== tempInputBackgroundFocus) {
      setUpdateBackgroundColor(true);
    }
  }, [tempInputBackgroundBlur]);

  const handleChangeFormWidth = (e) => {
    if (!isPremium()) {
      setModal(() => <EmbedLimitOptionsModal />);
    } else {
      setFormWidth(e);
    }
  };

  const handleChangeFormMaxHeight = (e) => {
    if (!isPremium()) {
      setModal(() => <EmbedLimitOptionsModal />);
    } else {
      setFormMaxHeight(e);
    }
  };

  return (
    <>
      <div className={`design-pop-up`}>
        <div className="body-tools">
          <div className="background-form-color part">
            <span>{getTextByKey("form-background-color")}</span>
            <div className="input-color-div">
              <div className="text-color">
                <input
                  onFocus={(e) =>
                    handleFocusBackgroundColorText(e.target.value)
                  }
                  onBlur={(e) => handleBlurBackgroundColorText(e.target.value)}
                  type="text"
                  value={backgroundColorText}
                  onChange={(e) => {
                    handleChangeBackgroundColorText(e.target.value);
                  }}
                />
              </div>
              <div
                className="background-color"
                style={{ backgroundColor: formBackgroundColor }}
              >
                <input
                  onFocus={(e) => setTempInputBackgroundFocus(e.target.value)}
                  onBlur={(e) => setTempInputBackgroundBlur(e.target.value)}
                  value={formBackgroundColor}
                  onChange={(e) => handleChangeBackgroundColor(e.target.value)}
                  type="color"
                />
              </div>
            </div>
          </div>
          <div className="form-width-size part">
            <div className={`width-input ${invalidWidth ? "invalid" : ""}`}>
              <div className="width-label">
                <span className="text">{getTextByKey("max-form-width")}</span>
                <Tooltip
                  end
                  id={"form-width-size"}
                  tooltipTextKey="max-form-width-tooltip"
                  //hardPositioned
                >
                  <Icon name="question-circle" iconClass="tooltip-question" />
                </Tooltip>
              </div>
              <div className="div-input">
                <input
                  onFocus={(e) => handleFocusWidth(e.target.value)}
                  onBlur={(e) => handleBlurWidth(e.target.value)}
                  type="number"
                  min={460}
                  max={1000}
                  value={formWidth}
                  onChange={(e) => handleChangeFormWidth(e.target.value)}
                />
                {invalidWidth ? (
                  <div className="invalid-width">
                    <span>
                      {getTextByKey(
                        formWidth < 460
                          ? "min-form-width-validate"
                          : formWidth > 1000
                          ? "max-form-width-validate"
                          : "invalid-width"
                      )}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="form-width-size part">
            <div className={`width-input ${invalidMaxHeight ? "invalid" : ""}`}>
              <div className="width-label">
                <span className="text">{getTextByKey("max-form-height")}</span>
                <Tooltip
                  end
                  id={"form-height-size"}
                  tooltipTextKey="max-form-height-tooltip"
                  //hardPositioned
                >
                  <Icon name="question-circle" iconClass="tooltip-question" />
                </Tooltip>
              </div>
              <div className="div-input">
                <input
                  onFocus={(e) => handleFocusMaxHeight(e.target.value)}
                  onBlur={(e) => handleBlurMaxHeight(e.target.value)}
                  type="number"
                  min={0}
                  value={formMaxHeight}
                  onChange={(e) => handleChangeFormMaxHeight(e.target.value)}
                />
                {invalidMaxHeight ? (
                  <div className="invalid-width">
                    <span>
                      {getTextByKey(
                        formMaxHeight < 450 && formMaxHeight > 0
                          ? "min-form-height-validate"
                          : "invalid-height"
                      )}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="external-website-alert">
        <div className="exclamation-icon">
          <Image
            name="create-form/exclamation-icon"
            ext="svg"
            alt=""
            title=""
          />
        </div>
        <div className="alert-text">
          <span>{getTextByKey("design-relevent-external-website")}</span>
        </div>
      </div>
    </>
  );
};

export default DesignTool;
